import React, {
  Component,
} from "react";

import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import * as PropTypes from "prop-types";

import {
  fetchViaPost,
} from "../../helpers/fetchViaPost";

import "../../parts/wdl.css";

import {
  HtmlTooltip,
} from "../../styled-components/HtmlTooltip";

import { prepareRecentGameDetail, formatRecentGameDate, prepareRecentGameScore } from "../../helpers/recentTeamGames";
export { formatRecentGameDate, prepareRecentGameScore, prepareRecentGameDetail } from "../../helpers/recentTeamGames";

export class LeagueTable extends Component {

  state = {
    expanded: false,
    loading: false,
    localStandings: null,
    visitorStandings: null,
  };

  componentDidMount() {
    if (this.props.autoLoadRecentGames) this.calc();
  }

  componentDidUpdate(previousProps) {
    if (this.props.autoLoadRecentGames && !previousProps.autoLoadRecentGames) this.calc();
  }


  getIcon() {
    if (
      this.state.loading
    ) {
      return (
        <CircularProgress
          size={16}
        />
      );
    }

    return this.state.expanded
      ? <ExpandLessIcon />
      : <ExpandMoreIcon />;
  }


  calc = async () => {
    if (
      this.state.loading
    ) {
      return;
    }

    /*
     * Bereits geladene Daten nur
     * ein-/ausblenden.
     */
    if (
      Array.isArray(
        this.state.localStandings
      ) &&
      Array.isArray(
        this.state.visitorStandings
      )
    ) {
      this.setState(
        (current) => ({
          expanded:
            !current.expanded,
        })
      );

      this.props.onRecentGamesLoaded?.({
        home: this.state.localStandings.map((item) => prepareRecentGameDetail(item, this.props.localteamId)),
        away: this.state.visitorStandings.map((item) => prepareRecentGameDetail(item, this.props.visitorteamId)),
      });

      return;
    }


    this.setState({
      loading: true,
    });


    try {
      const [
        localResponse,
        visitorResponse,
      ] =
        await Promise.all([
          fetchViaPost(
            "get-standings",
            JSON.stringify({
              season_id:
                this.props.seasonId,

              before_starting_at:
                this.props.beforeStartingAt,

              team_id:
                this.props.localteamId,
            })
          ),

          fetchViaPost(
            "get-standings",
            JSON.stringify({
              season_id:
                this.props.seasonId,

              before_starting_at:
                this.props.beforeStartingAt,

              team_id:
                this.props.visitorteamId,
            })
          ),
        ]);


      const [
        localStandings,
        visitorStandings,
      ] =
        await Promise.all([
          localResponse.json(),
          visitorResponse.json(),
        ]);


      this.setState({
        localStandings:
          Array.isArray(
            localStandings
          )
            ? localStandings
            : [],

        visitorStandings:
          Array.isArray(
            visitorStandings
          )
            ? visitorStandings
            : [],

        expanded: true,
      }, () => this.props.onRecentGamesLoaded?.({
        home: this.state.localStandings.map((item) => prepareRecentGameDetail(item, this.props.localteamId)),
        away: this.state.visitorStandings.map((item) => prepareRecentGameDetail(item, this.props.visitorteamId)),
      }));

    } finally {
      this.setState({
        loading: false,
      });
    }
  };


  drawWdlTable() {
    if (
      !this.state.expanded
    ) {
      return "";
    }

    const local =
      Array.isArray(
        this.state.localStandings
      )
        ? this.state.localStandings
        : [];

    const visitor =
      Array.isArray(
        this.state.visitorStandings
      )
        ? this.state.visitorStandings
        : [];


    const output = [];


    local.forEach(
      (item) => {
        output.push(
          this.getHtmlTooltip(
            item,
            `local-${item.fixture_id}`,
            this.props.localteamId
          )
        );
      }
    );


    output.push(
      <br
        key="wdl-separator"
      />
    );


    visitor.forEach(
      (item) => {
        output.push(
          this.getHtmlTooltip(
            item,
            `visitor-${item.fixture_id}`,
            this.props.visitorteamId
          )
        );
      }
    );


    return output;
  }


  formatDate(
    value
  ) {
    return formatRecentGameDate(value);
  }


  prepareScore(
    score
  ) {
    return prepareRecentGameScore(score);
  }


  getHtmlTooltip(
    item,
    key,
    teamId
  ) {
    const states = {
      L: "error",
      D: "warning",
      W: "success",
    };

    const detail = prepareRecentGameDetail(item, teamId);
    const { form, homeName, awayName, venue, score: scoreText } = detail;

    const tooltip = (
      <>
        <strong>
          {scoreText}
        </strong>

        <hr />

        {homeName} - {awayName} ({venue})

        <hr />

        {this.formatDate(
          detail.startingAt
        )}
      </>
    );


    return (
      <HtmlTooltip
        key={key}
        title={tooltip}
      >
        <Button
          variant="contained"
          size="small"
          color={
            states[form] ||
            "inherit"
          }
          className="cwi"
          aria-label={`${form || "–"}: ${homeName} - ${awayName}, ${venue}, ${scoreText}, ${detail.date}`}
        >
          {form || "–"}
        </Button>
      </HtmlTooltip>
    );
  }


  render() {
    if (
      this.props.stats
        ?.length === 0
    ) {
      return null;
    }

    const stats =
      this.props.stats[
        this.props.localteamId
      ];

    const statsV =
      this.props.stats[
        this.props.visitorteamId
      ];


    return (
      <Table>
        <TableHead>
          <TableRow>
            <th
              colSpan={2}
              align="left"
              style={{
                width: "30%",
              }}
            >
              League table
            </th>

            <th align="left">
              SP
            </th>

            <th align="left">
              S
            </th>

            <th align="left">
              U
            </th>

            <th align="left">
              N
            </th>

            <th align="left">
              Goals
            </th>

            <th align="left">
              Diff
            </th>

            <th align="left">
              Points
            </th>

            <th align="left">
              WDL{" "}

              <Button
                variant="contained"
                style={{
                  backgroundColor:
                    "transparent",
                }}
                size="small"
                sx={{
                  color: "black",
                }}
                className="cwi"
                onClick={
                  this.calc
                }
                title="Letzte Spiele dieser Saison"
              >
                {this.getIcon()}
              </Button>
            </th>
          </TableRow>
        </TableHead>


        <TableBody>
          <TableRow>
            <th>
              {stats?.league.position}
            </th>

            <td>
              {this.props.localTeam.name}
            </td>

            <td>
              {stats?.league.games}
            </td>

            <td>
              {stats?.league.won}
            </td>

            <td>
              {stats?.league.draw}
            </td>

            <td>
              {stats?.league.lost}
            </td>

            <td>
              {stats?.league.goals}
            </td>

            <td>
              {stats?.league.diff}
            </td>

            <td>
              {stats?.league.points}
            </td>

            <td
              rowSpan={2}
              colSpan={2}
            >
              {this.drawWdlTable()}
            </td>
          </TableRow>


          <TableRow>
            <th>
              {statsV?.league.position}
            </th>

            <td>
              {this.props.visitorTeam.name}
            </td>

            <td>
              {statsV?.league.games}
            </td>

            <td>
              {statsV?.league.won}
            </td>

            <td>
              {statsV?.league.draw}
            </td>

            <td>
              {statsV?.league.lost}
            </td>

            <td>
              {statsV?.league.goals}
            </td>

            <td>
              {statsV?.league.diff}
            </td>

            <td>
              {statsV?.league.points}
            </td>

            <td />
          </TableRow>


          <TableRow>
            <th
              colSpan={2}
              align="left"
              style={{
                width: "50%",
              }}
            >
              Home table
            </th>

            <th align="left">
              SP
            </th>

            <th align="left">
              S
            </th>

            <th align="left">
              U
            </th>

            <th align="left">
              N
            </th>

            <th align="left">
              Goals
            </th>

            <th align="left">
              Diff
            </th>

            <th align="left">
              Points
            </th>
          </TableRow>


          <TableRow
            sx={{
              backgroundColor:
                "rgb(255,181,99,0.2)",
            }}
          >
            <th>
              {stats?.home.position}
            </th>

            <td>
              {this.props.localTeam.name}
            </td>

            <td>
              {stats?.home.games}
            </td>

            <td>
              {stats?.home.won}
            </td>

            <td>
              {stats?.home.draw}
            </td>

            <td>
              {stats?.home.lost}
            </td>

            <td>
              {stats?.home.goals}
            </td>

            <td>
              {stats?.home.diff}
            </td>

            <td>
              {stats?.home.points}
            </td>
          </TableRow>


          <TableRow>
            <th
              colSpan={2}
              align="left"
              style={{
                width: "50%",
              }}
            >
              Away table
            </th>

            <th align="left">
              SP
            </th>

            <th align="left">
              S
            </th>

            <th align="left">
              U
            </th>

            <th align="left">
              N
            </th>

            <th align="left">
              Goals
            </th>

            <th align="left">
              Diff
            </th>

            <th align="left">
              Points
            </th>
          </TableRow>


          <TableRow>
            <th>
              {statsV?.away.position}
            </th>

            <td>
              {this.props.visitorTeam.name}
            </td>

            <td>
              {statsV?.away.games}
            </td>

            <td>
              {statsV?.away.won}
            </td>

            <td>
              {statsV?.away.draw}
            </td>

            <td>
              {statsV?.away.lost}
            </td>

            <td>
              {statsV?.away.goals}
            </td>

            <td>
              {statsV?.away.diff}
            </td>

            <td>
              {statsV?.away.points}
            </td>
          </TableRow>
        </TableBody>
      </Table>
    );
  }
}


LeagueTable.propTypes = {
  stats:
    PropTypes.any,

  seasonId:
    PropTypes.any,

  localteamId:
    PropTypes.any,

  localTeam:
    PropTypes.any,

  visitorteamId:
    PropTypes.any,

  visitorTeam:
    PropTypes.any,
};
